import './TeaserInfoModal.scss';
import React, { useRef, useState } from 'react';
import { Row, Col, FormGroup, ModalBody, ModalHeader, Label } from 'reactstrap';
import Rodal from '../../../components/Rodal/Rodal';
import ButtonLoader from '../../../components/Layout/Buttons/ButtonLoader';
import { Form, Input } from '@availity/form';
import createNotification from '../../../utils/createNotification';
import { FormikErrors, FormikProps, FormikTouched } from 'formik';
import * as yup from 'yup';
import { TeaserPracticeData } from '../../../slices/marketplace/timeline/models/teaser-data.model';
import { LocationTypes } from '../../../slices/marketplace/timeline/models/enums/location-type.enum';
import { OwnershipTypes } from '../../../slices/marketplace/timeline/models/enums/ownership-type.enum';
import { RevenueGrowthRates } from '../../../slices/marketplace/timeline/models/enums/revenue-growth.enum';
import { useStateSelector } from '../../../store/selectors';
import ConfirmationModal from '../../../components/Modal/ConfirmationModal';
import { useCurrentMarketplaceParty } from '../../../custom-hooks/useCurrentMarketplaceParty';
import { TimelineParty } from '../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import StateSelectField from '../../../components/FormFields/StateSelectField/StateSelectField';
import Helpers from '../../../utils/helper';
import { useServiceFocuses } from '../../../custom-hooks/useServiceFocuses';
import {
    getOtherFocus,
    isOtherFocus,
} from '../../UserPages/Login/Modals/PracticeRegistrationForm/utils';

interface TeaserInfoModalProps {
    timelineId: number;
    teaserData: TeaserPracticeData[];
    isReadOnly: boolean;
    isVisible: boolean;
    onClose: () => void;
    onSaveToTempState?: (newValues: TeaserPracticeData[]) => Promise<void>;
    onUpdateConfirm?: () => void;
    fetchData?: () => void;
}

const TeaserInfoModal = (props: TeaserInfoModalProps) => {
    let modalForm = useRef<FormikProps<{ teaser: TeaserPracticeData[] }>>(null);
    const serviceFocuses = useServiceFocuses();

    const axios = useStateSelector((state) => state.core.axios);

    const [isSaving, setIsSaving] = useState(false);
    const [isConfirming, setIsConfirming] = useState(false);

    const [isConfirmationPopupVisible, setIsConfirmationPopupVisible] =
        useState(false);

    const [activeTab, setActiveTab] = useState<number>(
        props.teaserData && props.teaserData.length > 0
            ? props.teaserData[0]?.practiceId
            : 0
    );

    const [practicesData, setPracticesData] = useState<TeaserPracticeData[]>([
        ...props.teaserData.map((practice) =>
            !practice.locations || practice.locations.length == 0
                ? {
                      ...practice,
                      practiceType: undefined,
                      otherPracticeType: undefined,
                      revenueGrowthRate: undefined,
                      ownershipType: undefined,
                      locations: [
                          {
                              id: 0,
                              practiceId: practice.practiceId,
                              fteDvms: undefined,
                              locationType: undefined,
                              practiceType: undefined,
                              otherPracticeType: undefined,
                              state: undefined,
                              dbaName: undefined,
                          },
                      ],
                  }
                : practice
        ),
    ]);

    const party = useCurrentMarketplaceParty(props.timelineId);
    const isAdmin = party === TimelineParty.VetValue;
    const isBroker = party === TimelineParty.Broker;

    const updatePracticeData = (
        practiceId: number,
        locationId: number | null,
        field: string,
        value: string
    ) => {
        setPracticesData((prev) =>
            prev.map((practice) =>
                practice.practiceId === practiceId
                    ? locationId !== null
                        ? {
                              ...practice,
                              locations: practice.locations.map((loc) =>
                                  loc.id === locationId
                                      ? { ...loc, [field]: value }
                                      : loc
                              ),
                          }
                        : { ...practice, [field]: value }
                    : practice
            )
        );
    };

    const renderServiceFocusField = (
        practiceIndex: number,
        locationIndex: number | null,
        practiceId: number,
        locationId: number | null,
        currentValue: number | undefined,
        otherValue: string | undefined,
        isReadOnly: boolean
    ) => {
        const fieldName =
            locationIndex !== null
                ? `teaser[${practiceIndex}].locations[${locationIndex}].practiceType`
                : `teaser[${practiceIndex}].practiceType`;
        const otherFieldName =
            locationIndex !== null
                ? `teaser[${practiceIndex}].locations[${locationIndex}].otherPracticeType`
                : `teaser[${practiceIndex}].otherPracticeType`;

        return (
            <Row>
                <Col md={6}>
                    <FormGroup>
                        <Label for="practiceType">Service Focus:</Label>
                        <Input
                            type="select"
                            id={fieldName}
                            name={fieldName}
                            disabled={isReadOnly}
                            onChange={(event) => {
                                const prevOther = isOtherFocus(
                                    serviceFocuses,
                                    Number(currentValue)
                                );
                                const newNotOther = !isOtherFocus(
                                    serviceFocuses,
                                    Number(event.target.value)
                                );

                                if (prevOther && newNotOther) {
                                    updatePracticeData(
                                        practiceId,
                                        locationId,
                                        'otherPracticeType',
                                        undefined
                                    );
                                    modalForm.current.setFieldValue(
                                        otherFieldName,
                                        undefined,
                                        false
                                    );
                                }

                                updatePracticeData(
                                    practiceId,
                                    locationId,
                                    'practiceType',
                                    event.target.value
                                );
                                modalForm.current.setFieldValue(
                                    fieldName,
                                    event.target.value,
                                    false
                                );
                            }}
                            onBlur={(e) => {}}
                            value={currentValue || 0}>
                            <option
                                disabled={true}
                                value={0}
                                key={'practiceType0'}>
                                Select Service Focus
                            </option>
                            {serviceFocuses.map((item) => (
                                <option
                                    value={item.id}
                                    key={'practiceType' + item.id}>
                                    {item.name}
                                </option>
                            ))}
                        </Input>
                    </FormGroup>
                </Col>
                <Col md={6}>
                    {currentValue &&
                    isOtherFocus(serviceFocuses, currentValue) ? (
                        <FormGroup>
                            <Label for="otherPracticeType">
                                Please Specify:
                            </Label>
                            <Input
                                type="text"
                                id={otherFieldName}
                                name={otherFieldName}
                                disabled={isReadOnly}
                                className="text-field"
                                onChange={(e) => {
                                    updatePracticeData(
                                        practiceId,
                                        locationId,
                                        'otherPracticeType',
                                        e.target.value
                                    );
                                    modalForm.current.setFieldValue(
                                        otherFieldName,
                                        e.target.value,
                                        false
                                    );
                                }}
                                onBlur={(e) => {}}
                                value={otherValue || ''}
                            />
                        </FormGroup>
                    ) : (
                        []
                    )}
                </Col>
            </Row>
        );
    };

    const renderTabContent = () => {
        if (practicesData.length == 0) return <>No Data</>;

        const selectedPractice = practicesData.find(
            (p) => p.practiceId == activeTab
        );
        const practiceIndex = practicesData.indexOf(selectedPractice);

        return (
            <div
                className="practice-tab-content"
                key={selectedPractice.practiceId}>
                <div className="practice-level-fields mb-4">
                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="ownershipType">Ownership:</Label>
                                <Input
                                    type="select"
                                    id={`teaser[${practiceIndex}].ownershipType`}
                                    name={`teaser[${practiceIndex}].ownershipType`}
                                    disabled={props.isReadOnly}
                                    onChange={(event) => {
                                        updatePracticeData(
                                            selectedPractice.practiceId,
                                            null,
                                            'ownershipType',
                                            event.target.value
                                        );
                                        modalForm.current.setFieldValue(
                                            `teaser[${practiceIndex}].ownershipType`,
                                            event.target.value,
                                            false
                                        );
                                    }}
                                    onBlur={(e) => {}}
                                    defaultValue={0}>
                                    <option
                                        disabled={true}
                                        value={0}
                                        key={'ownershipType0'}>
                                        Select Ownership
                                    </option>
                                    {OwnershipTypes.map((item) => (
                                        <option
                                            value={item.id}
                                            key={'ownershipType' + item.id}>
                                            {item.name}
                                        </option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="revenueGrowthRate">
                                    Revenue Growth Rate:
                                </Label>
                                <Input
                                    type="select"
                                    id={`teaser[${practiceIndex}].revenueGrowthRate`}
                                    name={`teaser[${practiceIndex}].revenueGrowthRate`}
                                    disabled={props.isReadOnly}
                                    onChange={(event) => {
                                        updatePracticeData(
                                            selectedPractice.practiceId,
                                            null,
                                            'revenueGrowthRate',
                                            event.target.value
                                        );
                                        modalForm.current.setFieldValue(
                                            `teaser[${practiceIndex}].revenueGrowthRate`,
                                            event.target.value,
                                            false
                                        );
                                    }}
                                    onBlur={(e) => {}}
                                    defaultValue={0}>
                                    <option
                                        disabled={true}
                                        value={0}
                                        key={'revenueGrowthRate0'}>
                                        Select Range of Growth Rate
                                    </option>
                                    {RevenueGrowthRates.map((item) => (
                                        <option
                                            value={item.id}
                                            key={'revenueGrowthRate' + item.id}>
                                            {item.name}
                                        </option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                    </Row>
                    {renderServiceFocusField(
                        practiceIndex,
                        null,
                        selectedPractice.practiceId,
                        null,
                        selectedPractice.practiceType,
                        selectedPractice.otherPracticeType,
                        props.isReadOnly
                    )}
                </div>
                {selectedPractice?.locations &&
                selectedPractice?.locations?.length > 0 ? (
                    <>
                        {selectedPractice?.locations?.map(
                            (location, locationIndex) => {
                                return (
                                    <div key={location.id}>
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <div className="location-title">{`Location ${
                                                locationIndex + 1
                                            }`}</div>
                                        </div>
                                        <Row>
                                            <Col md={6}>
                                                <FormGroup>
                                                    <Label for="name">
                                                        DBA Name:
                                                    </Label>
                                                    <Input
                                                        type="text"
                                                        id={`teaser[${practiceIndex}].locations[${locationIndex}].dbaName`}
                                                        name={`teaser[${practiceIndex}].locations[${locationIndex}].dbaName`}
                                                        disabled={
                                                            props.isReadOnly
                                                        }
                                                        className="text-field"
                                                        onChange={(e) => {
                                                            updatePracticeData(
                                                                selectedPractice.practiceId,
                                                                location.id,
                                                                'dbaName',
                                                                e.target.value
                                                            );
                                                            modalForm.current.setFieldValue(
                                                                `teaser[${practiceIndex}].locations[${locationIndex}].dbaName`,
                                                                e.target.value,
                                                                false
                                                            );
                                                        }}
                                                        onBlur={(e) => {}}
                                                        placeholder="Enter DBA Name"
                                                    />
                                                </FormGroup>
                                            </Col>
                                            <Col md={6}>
                                                <FormGroup>
                                                    <Label for="name">
                                                        State / Province:
                                                    </Label>
                                                    <StateSelectField
                                                        name={`teaser[${practiceIndex}].locations[${locationIndex}].state`}
                                                        value={
                                                            location.state || ''
                                                        }
                                                        disabled={
                                                            props.isReadOnly
                                                        }
                                                        label=""
                                                        placeholder="Select State"
                                                        showLongFormat={true}
                                                        onChange={(event) => {
                                                            updatePracticeData(
                                                                selectedPractice.practiceId,
                                                                location.id,
                                                                'state',
                                                                event.target
                                                                    .value
                                                            );
                                                            modalForm.current.setFieldValue(
                                                                `teaser[${practiceIndex}].locations[${locationIndex}].state`,
                                                                event.target
                                                                    .value,
                                                                false
                                                            );
                                                        }}
                                                    />
                                                </FormGroup>
                                            </Col>
                                            <Col md={6}></Col>
                                        </Row>
                                        <Row>
                                            <Col md={6}>
                                                <FormGroup>
                                                    <Label for="name">
                                                        Number of FTE DVMs:
                                                    </Label>
                                                    <Input
                                                        type="select"
                                                        id={`teaser[${practiceIndex}].locations[${locationIndex}].fteDvms`}
                                                        name={`teaser[${practiceIndex}].locations[${locationIndex}].fteDvms`}
                                                        disabled={
                                                            props.isReadOnly
                                                        }
                                                        className="text-field"
                                                        onChange={(e) => {
                                                            updatePracticeData(
                                                                selectedPractice.practiceId,
                                                                location.id,
                                                                'fteDvms',
                                                                e.target.value
                                                            );
                                                            modalForm.current.setFieldValue(
                                                                `teaser[${practiceIndex}].locations[${locationIndex}].fteDvms`,
                                                                e.target.value,
                                                                false
                                                            );
                                                        }}
                                                        placeholder="Enter Number of FTE DVMs">
                                                        {Helpers.getFteDvmsNumberList().map(
                                                            (option) => (
                                                                <option
                                                                    key={
                                                                        option.id
                                                                    }
                                                                    value={
                                                                        option.id
                                                                    }>
                                                                    {
                                                                        option.value
                                                                    }
                                                                </option>
                                                            )
                                                        )}
                                                    </Input>
                                                </FormGroup>
                                            </Col>
                                            <Col md={6}>
                                                <FormGroup>
                                                    <Label for="name">
                                                        Location Type:
                                                    </Label>
                                                    <Input
                                                        type="select"
                                                        id={`teaser[${practiceIndex}].locations[${locationIndex}].locationType`}
                                                        name={`teaser[${practiceIndex}].locations[${locationIndex}].locationType`}
                                                        disabled={
                                                            props.isReadOnly
                                                        }
                                                        onChange={(event) => {
                                                            updatePracticeData(
                                                                selectedPractice.practiceId,
                                                                location.id,
                                                                'locationType',
                                                                event.target
                                                                    .value
                                                            );
                                                            modalForm.current.setFieldValue(
                                                                `teaser[${practiceIndex}].locations[${locationIndex}].locationType`,
                                                                event.target
                                                                    .value,
                                                                false
                                                            );
                                                        }}
                                                        onBlur={(e) => {}}
                                                        defaultValue={0}>
                                                        <option
                                                            disabled={true}
                                                            value={0}
                                                            key={
                                                                'locationType0'
                                                            }>
                                                            Select Location Type
                                                        </option>
                                                        {LocationTypes.map(
                                                            (item) => (
                                                                <option
                                                                    value={
                                                                        item.id
                                                                    }
                                                                    key={
                                                                        'locationType' +
                                                                        item.id
                                                                    }>
                                                                    {item.name}
                                                                </option>
                                                            )
                                                        )}
                                                    </Input>
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                        {renderServiceFocusField(
                                            practiceIndex,
                                            locationIndex,
                                            selectedPractice.practiceId,
                                            location.id,
                                            location.practiceType,
                                            location.otherPracticeType,
                                            props.isReadOnly
                                        )}
                                    </div>
                                );
                            }
                        )}
                    </>
                ) : (
                    <>No Data</>
                )}
            </div>
        );
    };

    const hasTabError = (
        touched: FormikTouched<TeaserPracticeData[]>,
        errors: FormikErrors<TeaserPracticeData[]>,
        index: number
    ) => {
        if (!errors || !touched) return false;

        if (Object.hasOwn(touched, index) && Object.hasOwn(errors, index)) {
            const practiceTouched = touched[index] as any;
            const practiceError = errors[index] as any;

            if (practiceTouched && practiceError) {
                for (const key of Object.keys(practiceTouched)) {
                    if (practiceTouched[key] && practiceError[key]) {
                        return true;
                    }
                }
            }

            const locationsTouched = touched[index]?.locations;
            const locationsErrors = errors[index]?.locations;

            if (locationsTouched && locationsErrors) {
                for (
                    let locationIndex = 0;
                    locationIndex < locationsTouched.length;
                    locationIndex++
                ) {
                    const locationTouched = locationsTouched[
                        locationIndex
                    ] as any;
                    const locationError = locationsErrors[locationIndex] as any;

                    if (locationTouched && locationError) {
                        for (const key of Object.keys(locationTouched)) {
                            if (locationTouched[key] && locationError[key]) {
                                return true;
                            }
                        }
                    }
                }
            }
        }

        return false;
    };

    const renderPracticeTabs = (
        errors: FormikErrors<TeaserPracticeData[]>,
        touched: FormikTouched<TeaserPracticeData[]>
    ) => {
        return (
            <div>
                <div className="RRT__container mt-10">
                    <div className="RRT__tabs body-tabs">
                        {practicesData.map(
                            (teaserPracticeData, practiceIndex) => {
                                let className =
                                    activeTab === teaserPracticeData.practiceId
                                        ? 'RRT__tab RRT__tab--first RRT__tab--selected'
                                        : 'RRT__tab';

                                className += hasTabError(
                                    touched,
                                    errors,
                                    practiceIndex
                                )
                                    ? ' mui-error'
                                    : '';

                                return (
                                    <div
                                        key={practiceIndex}
                                        className={className}
                                        onClick={() =>
                                            setActiveTab(
                                                teaserPracticeData.practiceId
                                            )
                                        }>
                                        {teaserPracticeData.practiceName}
                                    </div>
                                );
                            }
                        )}
                    </div>
                </div>
                {renderTabContent()}
            </div>
        );
    };

    const initSubmit = (confirm: boolean) => {
        const validationPromise = confirm
            ? modalForm.current.validateForm()
            : Promise.resolve(
                  {} as FormikErrors<{ teaser: TeaserPracticeData[] }>
              );
        validationPromise.then(
            (errors: FormikErrors<{ teaser: TeaserPracticeData[] }>) => {
                if (modalForm.current.isValid || !confirm) {
                    if (!confirm && isAdmin && props.onSaveToTempState) {
                        props
                            .onSaveToTempState([...practicesData])
                            .then(() => props.onClose())
                            .catch((error) => {});
                    } else {
                        if (confirm) {
                            setIsConfirming(true);
                            axios
                                .put(
                                    `/api/Marketplace/Engagements/${props.timelineId}/ConfirmTeaser`,
                                    { practiceData: practicesData }
                                )
                                .then((response: any) => {
                                    if (response && response.status === 200) {
                                        if (props.onUpdateConfirm)
                                            props.onUpdateConfirm();
                                        if (props.fetchData) props.fetchData();
                                        createNotification(
                                            'Teaser successfully confirmed.',
                                            'success'
                                        );
                                    } else {
                                        createNotification(
                                            'An error occurred while confirming Teaser.',
                                            'error'
                                        );
                                    }
                                })
                                .catch((error) => {
                                    createNotification(
                                        'An error occurred while confirming Teaser.',
                                        'error'
                                    );
                                })
                                .finally(() => {
                                    setIsConfirming(false);
                                    props.onClose();
                                });
                        } else {
                            setIsSaving(true);
                            axios
                                .put(
                                    `/api/Marketplace/Engagements/${props.timelineId}/teaser`,
                                    { practiceData: practicesData }
                                )
                                .then((response: any) => {
                                    if (response && response.status === 200) {
                                        if (props.fetchData) props.fetchData();
                                        createNotification(
                                            'Teaser Info successfully saved.',
                                            'success'
                                        );
                                    } else {
                                        createNotification(
                                            'An error occurred while saving Teaser Info.',
                                            'error'
                                        );
                                    }
                                })
                                .catch((error) => {
                                    createNotification(
                                        'An error occurred while saving Teaser Info.',
                                        'error'
                                    );
                                })
                                .finally(() => {
                                    setIsSaving(false);
                                    props.onClose();
                                });
                        }
                    }
                } else modalForm.current.handleSubmit();
            }
        );
    };

    return (
        <div className="teaser-info-modal">
            <Rodal
                visible={props.isVisible}
                animation={'fade'}
                onClose={props.onClose}
                showMask={true}
                width={600}
                closeOnEsc
                className="teaser-rodal"
                center>
                <ModalHeader className="popup-header">
                    <div className="d-flex align-items-center">
                        <span>Teaser Information</span>
                    </div>
                </ModalHeader>
                <ModalBody>
                    <div className="teaser-header-text">
                        Please review this teaser containing high-level
                        information about your practices for accuracy. Potental
                        buyers will use this information to access their
                        interest in your practices.
                    </div>

                    <Form
                        innerRef={modalForm}
                        initialValues={{ teaser: practicesData }}
                        validationSchema={yup.object().shape({
                            teaser: yup
                                .array()
                                .of(
                                    yup.object().shape({
                                        locations: yup
                                            .array()
                                            .of(
                                                yup.object().shape({
                                                    state: yup
                                                        .string()
                                                        .required(
                                                            'State is required'
                                                        ),
                                                    fteDvms: yup
                                                        .number()
                                                        .min(
                                                            1,
                                                            'FTE DVMs is required'
                                                        )
                                                        .required(
                                                            'Number of FTE DVMs is required'
                                                        ),
                                                    locationType: yup
                                                        .number()
                                                        .min(
                                                            1,
                                                            'Location Type is required'
                                                        )
                                                        .required(
                                                            'Location Type is required'
                                                        ),
                                                    practiceType: yup
                                                        .number()
                                                        .min(
                                                            1,
                                                            'Service Focus is required'
                                                        )
                                                        .required(
                                                            'Service Focus is required'
                                                        ),
                                                    otherPracticeType: yup
                                                        .string()
                                                        .when('practiceType', {
                                                            is: getOtherFocus(
                                                                serviceFocuses
                                                            ).id,
                                                            then: (schema) =>
                                                                schema.required(
                                                                    'Please specify other Service Focus.'
                                                                ),
                                                            otherwise: (
                                                                schema
                                                            ) =>
                                                                schema.notRequired(),
                                                        }),
                                                    dbaName: yup
                                                        .string()
                                                        .required(
                                                            'DBA Name is required'
                                                        ),
                                                })
                                            )
                                            .min(
                                                1,
                                                'At least one location is required'
                                            ),
                                        practiceType: yup
                                            .number()
                                            .min(1, 'Service Focus is required')
                                            .required(
                                                'Service Focus is required'
                                            ),
                                        otherPracticeType: yup
                                            .string()
                                            .when('practiceType', {
                                                is: getOtherFocus(
                                                    serviceFocuses
                                                ).id,
                                                then: (schema) =>
                                                    schema.required(
                                                        'Please specify other Service Focus.'
                                                    ),
                                            }),
                                        revenueGrowthRate: yup
                                            .number()
                                            .min(
                                                1,
                                                'Revenue Growth Rate is required'
                                            )
                                            .required(
                                                'Revenue Growth Rate is required'
                                            ),
                                        ownershipType: yup
                                            .number()
                                            .min(
                                                1,
                                                'Ownership Type is required'
                                            )
                                            .required(
                                                'Ownership Type is required'
                                            ),
                                    })
                                )
                                .min(1, 'At least one practice is required'),
                        })}
                        validateOnChange={false}
                        validateOnBlur={false}
                        onSubmit={(values, formikHelpers) => {}}>
                        {({ values, errors, touched }) => (
                            <div className="d-flex flex-column">
                                <FormGroup>
                                    {renderPracticeTabs(
                                        errors?.teaser as FormikErrors<
                                            TeaserPracticeData[]
                                        >,
                                        touched?.teaser
                                    )}
                                </FormGroup>

                                <div className="mt-auto d-flex justify-content-end buttons-wrapper">
                                    <button
                                        type="button"
                                        className="btn btn-cancel"
                                        onClick={() => props.onClose()}>
                                        Cancel
                                    </button>
                                    <ButtonLoader
                                        buttonText={`Save and Close`}
                                        loaderButtonText={''}
                                        disabled={props.isReadOnly}
                                        isLoading={isSaving}
                                        onClick={() => initSubmit(false)}
                                        type="button"
                                        className={`btn ${
                                            isAdmin || isBroker
                                                ? 'btn-primary'
                                                : 'btn-cancel'
                                        } loader-submit`}
                                    />
                                    {!isAdmin && !isBroker ? (
                                        <ButtonLoader
                                            buttonText={`Confirm Teaser`}
                                            loaderButtonText={''}
                                            disabled={
                                                props.isReadOnly || isAdmin
                                            }
                                            isLoading={isConfirming}
                                            onClick={() => {
                                                modalForm.current
                                                    .validateForm()
                                                    .then(() => {
                                                        if (
                                                            modalForm.current
                                                                .isValid
                                                        )
                                                            setIsConfirmationPopupVisible(
                                                                true
                                                            );
                                                        else
                                                            modalForm.current.handleSubmit();
                                                    });
                                            }}
                                            type="submit"
                                            className={
                                                'btn btn-primary loader-submit'
                                            }
                                        />
                                    ) : (
                                        []
                                    )}
                                </div>
                            </div>
                        )}
                    </Form>
                </ModalBody>
            </Rodal>
            {isConfirmationPopupVisible && (
                <ConfirmationModal
                    header="Confirm Teaser"
                    isVisible={isConfirmationPopupVisible}
                    setIsVisible={setIsConfirmationPopupVisible}
                    confirmationText="By clicking Confirm, your teaser will be posted for buyers to express interest. Please ensure all data is accurate before confirming."
                    params={''}
                    onConfirm={() => initSubmit(true)}
                    nextButtonText="Confirm"
                    cancelButtonText="Cancel"
                    isLoading={isConfirming}
                />
            )}
        </div>
    );
};

export default TeaserInfoModal;
